import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsApi } from '../api';
import { Mail, Send, Eye, MessageSquare, AlertTriangle, TrendingUp, Activity, Loader2, ArrowUpRight } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:shadow-sm transition-shadow">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <span className="text-sm text-[#5F6368] font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-[#202124]">{value}</p>
    {sub && <p className="text-xs text-[#9AA0A6] mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    analyticsApi.dashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A73E8]" /></div>;

  const d = data || {};
  const openRate = d.total_sent ? ((d.total_opened / d.total_sent) * 100).toFixed(1) : '0';
  const replyRate = d.total_sent ? ((d.total_replied / d.total_sent) * 100).toFixed(1) : '0';
  const bounceRate = d.total_sent ? ((d.total_bounced / d.total_sent) * 100).toFixed(1) : '0';

  return (
    <div data-testid="outreach-dashboard">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#202124]">Dashboard</h1>
        <p className="text-sm text-[#5F6368]">Overview of your outreach performance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Send} label="Total Sent" value={d.total_sent || 0} color="bg-[#1A73E8]" sub={`${d.sent_today || 0} sent today`} />
        <StatCard icon={Eye} label="Open Rate" value={`${openRate}%`} color="bg-[#1E8E3E]" sub={`${d.total_opened || 0} opened`} />
        <StatCard icon={MessageSquare} label="Reply Rate" value={`${replyRate}%`} color="bg-[#7C3AED]" sub={`${d.total_replied || 0} replies`} />
        <StatCard icon={AlertTriangle} label="Bounce Rate" value={`${bounceRate}%`} color="bg-[#D93025]" sub={`${d.total_bounced || 0} bounced`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Activity} label="Active Campaigns" value={d.active_campaigns || 0} color="bg-[#0891B2]" />
        <StatCard icon={TrendingUp} label="Total Campaigns" value={d.total_campaigns || 0} color="bg-[#F9AB00]" />
        <StatCard icon={Mail} label="Total Leads" value={d.total_leads || 0} color="bg-[#6366F1]" />
      </div>

      {/* Daily sends chart */}
      {d.daily_sends?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#202124] mb-4">Emails Sent (Last 30 Days)</h3>
          <div className="flex items-end gap-1 h-32">
            {d.daily_sends.map((day, i) => {
              const max = Math.max(...d.daily_sends.map(s => s.count));
              const h = max ? (day.count / max) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${day.date}: ${day.count}`}>
                  <div className="w-full bg-[#1A73E8] rounded-t opacity-80 hover:opacity-100 transition-opacity" style={{ height: `${Math.max(h, 2)}%` }} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top campaigns */}
      {d.top_campaigns?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F1F3F4]">
            <h3 className="text-sm font-semibold text-[#202124]">Top Campaigns</h3>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#F1F3F4] text-left">
              <th className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">Name</th>
              <th className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">Sent</th>
              <th className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">Opened</th>
              <th className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">Replied</th>
              <th className="px-5 py-3 text-xs font-semibold text-[#5F6368] uppercase tracking-wide">Status</th>
            </tr></thead>
            <tbody>
              {d.top_campaigns.map((c) => (
                <tr key={c.id} className="border-b border-[#F1F3F4] hover:bg-[#F8F9FA] transition-colors cursor-pointer" onClick={() => navigate(`/outreach/campaigns/${c.id}`)}>
                  <td className="px-5 py-3 text-[#202124] font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-[#5F6368]">{c.sent}</td>
                  <td className="px-5 py-3 text-[#5F6368]">{c.opened}</td>
                  <td className="px-5 py-3 text-[#5F6368]">{c.replied}</td>
                  <td className="px-5 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const STATUS_MAP = {
  draft: { bg: 'bg-[#E8EAED]', text: 'text-[#5F6368]' },
  active: { bg: 'bg-[#E6F4EA]', text: 'text-[#1E8E3E]' },
  paused: { bg: 'bg-[#FEF7E0]', text: 'text-[#F9AB00]' },
  completed: { bg: 'bg-[#E8F0FE]', text: 'text-[#1A73E8]' },
  stopped: { bg: 'bg-[#FCE8E6]', text: 'text-[#D93025]' },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{status}</span>;
}
